import { ListKeyManagerOption } from '@angular/cdk/a11y';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@vendasta/atlas';
import { combineLatest, firstValueFrom } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LOCATION_SWITCH_CONFIG_TOKEN } from '../../../../business-nav.module';
import { BusinessNavConfigService, Config } from '../../../../config.service';
import { SnackbarService } from '../../../../core-components/snackbar/snackbar.service';
import { BusinessNavDataService } from '../../../../data.service';
import { ProductLogoBankService } from '../../../../product-logo-bank.service';
import { BusinessNavLocationService } from '../../location.service';

enum AccountType {
  brand = 'account/brands',
  location = 'account/location',
  unset = 'account/unset',
}
const AccountTypes = Object.values(AccountType);

const ID_PLACEHOLDER = 'id_placeholder';

@Component({
  selector: 'atlas-location-list-item',
  templateUrl: './location-list-item.component.html',
  styleUrls: ['./location-list-item.component.scss'],
})
export class LocationListItemComponent implements OnInit, ListKeyManagerOption {
  @Input() location: Location;
  @Input() isActive = false;
  @Output() selected = new EventEmitter();
  /** Never disabled for now, this is here for the interface implementation. */
  disabled = false;
  protected isCurrentLocation = false;
  protected serviceProviderId: string;

  private readonly businessNavLocationService = inject(BusinessNavLocationService);

  protected logoUrl$ = this.config.config$.pipe(
    map((config: Config) => this.logobank.getLogo(config.serviceProviderId)),
  );

  protected hasProduct$ = combineLatest([this.businessNavLocationService.activePath$, this.config.config$]).pipe(
    map(([, config]) => {
      if (!this.location.accountGroup || !this.location.accountGroup.activatedProductIds) {
        return false;
      }

      return this.location.accountGroup.activatedProductIds.some(
        (activatedProductId) => activatedProductId === config.serviceProviderId,
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly defaultLocation$ = this.dataService.defaultLocation$;
  protected readonly isDefault$ = this.defaultLocation$.pipe(
    map(
      (defaultLocation) =>
        (this.location.accountGroup && defaultLocation?.accountGroupId === this.location.accountGroup.accountGroupId) ||
        (this.location.brand && defaultLocation?.groupId === this.location.brand.pathNodes.join('|')),
    ),
  );

  private readonly locationSwitchConfig = inject(LOCATION_SWITCH_CONFIG_TOKEN);

  constructor(
    public dataService: BusinessNavDataService,
    public config: BusinessNavConfigService,
    private snackbarService: SnackbarService,
    private t: TranslateService,
    private router: Router,
    private elRef: ElementRef,
    private readonly logobank: ProductLogoBankService,
  ) {
    firstValueFrom(this.config.config$).then((config) => {
      this.serviceProviderId = config.serviceProviderId;
    });
  }

  ngOnInit(): void {
    firstValueFrom(this.config.config$).then((config) => {
      this.isCurrentLocation =
        (!!this.location.accountGroup && config.accountId === this.location.accountGroup.accountGroupId) ||
        (!!this.location.brand && config.groupPath === this.location.brand.pathNodes.join('|'));
    });
  }

  setDefaultLocation(accountGroupId?: string, groupId?: string): void {
    this.dataService.setDefaultLocation(accountGroupId, groupId).subscribe({
      error: () => this.snackbarService.show(this.t.instant('COMMON.RESULT.FAILURE'), 'OK'),
    });
  }

  /** Gets the label for this option. */
  getLabel(): string {
    if (this.location.accountGroup) {
      return this.location.accountGroup.name;
    } else if (this.location.brand) {
      return this.location.brand.name;
    }
    // Location not provided yet, should probably not get here
    return '';
  }

  goToLocation(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selected.emit();
    if (this.isCurrentLocation) {
      return;
    }

    const params = collectRouteParams(this.router);

    const configForContext = this.locationSwitchConfig.supportedLocationContextConfig.find(
      (config) => config.context === (this.location.accountGroup ? 'location' : 'brand'),
    );

    if (!configForContext) {
      const url = this.location.accountGroup?.url || this.location.brand?.url;
      window.location.href = url + '/dashboard';
      return;
    }

    const isBlockListed = this.locationSwitchConfig.blockListedPaths.some((path) => path.test(this.router.url));
    const replaceId = params[configForContext.locationParamKey] || ID_PLACEHOLDER;
    if (isBlockListed || !this.router.url.includes(replaceId)) {
      const url = this.router.createUrlTree([this.locationSwitchConfig.defaultPath]).toString();
      this.navigateToUrl(this.interpolateUrl(url, ID_PLACEHOLDER));
      return;
    }

    const url = this.interpolateUrl(this.router.url, replaceId);
    this.navigateToUrl(url);
  }

  onScroll(): void {
    this.elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private interpolateUrl(url: string, replaceId: string): string {
    const useCategory = this.location.accountGroup ? AccountType.location : AccountType.brand;
    url = AccountTypes.reduce((acc, category) => acc.replace(category, useCategory), url);

    const useId = this.location.accountGroup
      ? this.location.accountGroup.accountGroupId
      : this.location.brand.pathNodes.join('|');

    return url.replace(replaceId, useId);
  }

  private navigateToUrl(url: string): void {
    if (this.serviceProviderId.toUpperCase() === 'VBC') {
      this.router.navigateByUrl(url);
    } else {
      window.location.href = url;
    }
  }
}

function collectRouteParams(router: Router) {
  let params = {};
  const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];
  while (stack.length > 0) {
    const route = stack.pop();
    params = { ...params, ...route.params };
    stack.push(...route.children);
  }
  return params;
}
